<script lang="ts">
  import {onMount} from 'svelte';
  import {httpClient} from '../../../../utils/http-client';
  import QuoteElement from '@/components/quote-verses/QuoteElement.svelte';
  import {user, isMobile} from '@/store';
  import Swal from 'sweetalert2';
  import {Video} from '@/types/project';
  import {download} from '@/utils/common';

  export let selectedVideo: Video | null;

  const type = 'Quotes & Verses';
  let isLoading = true;
  let quotes: any[] = [];
  let verses: any[] = [];
  let warningMessage: string = '';

  const handleDownload = async () => {
    const filename = `[${type}] ${selectedVideo?.title}.txt`;
    function stripHTML(input: string) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = input;
      return tempDiv.textContent || tempDiv.innerText || '';
    }
    const text = [
      'Quotes',
      quotes.map((quote, index) => `${index + 1}. "${stripHTML(quote)}"`).join('\n'),
      '',
      'Verses',
      verses.map((verse, index) => `${index + 1}. ${verse}`).join('\n')
    ].join('\n');

    await download(filename, text, type);
  };

  async function regenerate() {
    if (!selectedVideo) throw 'selectedVideo is null';
    isLoading = true;

    await httpClient.get(`/sermon-summary/${selectedVideo.id}/quotes/regenerate`).then(response => {
      quotes = response.data.quotes;
      verses = response.data.verses;
      isLoading = false;
    });
  }

  onMount(() => {
    httpClient.get(`/sermon-summary/${selectedVideo?.id}/quotes`).then(response => {
      quotes = response?.data?.quotes ? response.data.quotes : [];
      verses = response?.data?.verses ? response.data.verses : [];
      if(quotes.length === 0 && verses.length === 0) {
        if(selectedVideo?.metadata && selectedVideo?.metadata?.duration < 300) {
          warningMessage = "The video you've uploaded is too short for our content generation system to work effectively. Please upload a video that's at least 5 minutes long to get AI-powered content.";
        } else {
          warningMessage = "Failed to Generate Quotes and Verses.";
        }
      }
      isLoading = false;
    });
  });
</script>

<div class="h-full w-full">
  <div class="flex h-16 w-full items-center justify-between border-b border-b-gray-200 px-4 dark:border-b-zinc-600">
    {#if !$isMobile}
      <h2 class="text-md w-full font-bold text-gray-900">{type}</h2>
    {/if}
    <div class="flex w-full justify-center space-x-2 py-2 lg:justify-end lg:py-0">
      {#if $user.isImpersonated || $user.isAdmin}
        <button
          type="button"
          on:click={() => regenerate()}
          class="w-full rounded-md px-3 lg:w-auto py-1{isLoading
            ? 'disabled cursor-progress bg-zinc-400 text-white'
            : 'hoversermonbg border border-primary bg-white text-primary hover:bg-primary/10'}"
          disabled={isLoading}><i class="fa-solid fa-repeat"></i>&nbsp; Regenerate</button
        >
      {/if}
      <button
        on:click={() => handleDownload()}
        class="px-3 py-1 text-zinc-100 {isLoading
          ? 'bg-zinc-400'
          : 'hoversermonbg bg-primary'} w-full rounded-md lg:w-auto"
        disabled={isLoading}
      >
        <i class="fa-solid fa-download"></i>&nbsp; Download
      </button>
    </div>
  </div>
  <div class="h-[calc(100%-4rem)] w-full overflow-auto p-4">
    {#if isLoading}
      <div class="flex h-full w-full justify-center">
        <div class="flex flex-col items-center gap-4 pt-10">
          <div
            class="spinner h-8 w-8 !border-zinc-200 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
          />
          <h1 class="text-3xl font-bold text-zinc-400 dark:text-zinc-400">Loading Quotes...</h1>
        </div>
      </div>
    {:else if quotes && quotes.length > 0}
      <div class="flex h-full w-full justify-center">
        <div class="h-full w-full max-w-[50rem]">
          <b class="quoteTitle">Quotes</b>
          <div class="flex-justify flex w-full flex-row flex-wrap gap-6">
            {#each quotes as quote, index}
              <QuoteElement {quote} bind:selectedVideo />
              <!-- <div class="quote-block">
                            <p class="quoteText">{index + 1}. "{quote}"</p>
                        </div> -->
            {/each}
          </div>
          {#if verses && verses.length > 0}
            <b class="quoteTitle mt-4">Verses</b>
            {#each verses as verse, index}
              <div class="quote-block">
                <p class="quoteText">{index + 1}. {verse}</p>
              </div>
            {/each}
          {/if}
        </div>
      </div>
    {:else}
      <div class="flex h-full w-full items-center justify-center">
        <div class="flex flex-col items-center justify-center gap-4">
          <h1 class="pt-10 text-3xl font-bold text-zinc-400 dark:text-zinc-400">No Quotes Found</h1>
          <p class="text-zinc-400 dark:text-zinc-400">{warningMessage}</p>
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  .quoteTitle {
    @apply mb-6 block py-2 text-2xl font-extrabold;
  }

  .quoteText {
    @apply text-lg;
  }

  .quote-block {
    @apply pb-4 pl-10;
  }
</style>
